import type {
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  parseOptionalISODate,
} from '@b-stock/fe-core/bstock-react'

import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import {
  TlcBreakdown,
  TlcBreakdownSignInNotice,
} from '@/components/common/TlcBreakdown'
import { TlcStats } from '@/components/common/TlcStats'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { ContractInfoBanner } from '../../ContractInfoBanner'
import { MakeAnOfferFormProvider } from '../../offering/SimpleOfferActionModule/MakeAnOfferFormProvider'
import { useOfferAnalytics } from '../../offering/useOfferAnalytics'
import { useOfferTlc } from '../../offering/useOfferTlc'
import { ActionCardFooter } from '../ActionCardFooter'
import {
  ActionCard,
  ActionCardBody,
  SectionHeader,
  SectionInfoIcon,
} from '../ui'
import { SimpleOfferActions } from './SimpleOfferActions'
import { SimpleOfferHeader } from './SimpleOfferHeader'
import { SimpleOfferStatus } from './SimpleOfferStatus'
import { SimpleOfferSubmit } from './SimpleOfferSubmit'
import { getIsSpotPriceOffer } from './useOfferAmount'
import { useOfferDecisionState } from './useOfferDecisionState'

const ns = 'Listing.actionColumn.actionCard.offer'

export const SimpleOfferActionCard = ({
  listing,
  selectedQuote,
}: {
  listing: OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
}) => (
  <MakeAnOfferFormProvider listing={listing} selectedQuote={selectedQuote}>
    <SimpleOfferActionCardContent
      listing={listing}
      selectedQuote={selectedQuote}
    />
  </MakeAnOfferFormProvider>
)

const SimpleOfferActionCardContent = ({
  listing,
  selectedQuote,
}: {
  listing: OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
}) => {
  const { currency = 'USD', offering } = listing
  const isLoggedIn = useIsLoggedIn()
  const { trackButtonClicked } = useAnalytics()
  const getOfferAnalytics = useOfferAnalytics(listing)

  const {
    basis,
    bstockFee,
    feePercent,
    isWithoutShipping,
    suppressShippingLine,
    msrpPrice,
    shippingCents,
    tlcPercentOfMsrpWithTax,
    tlcPerUnitWithTax,
    tlcTaxInfo,
    tlcVatInfo,
    totalLandedCostWithTax,
    units,
  } = useOfferTlc(listing, selectedQuote)

  const state = useOfferDecisionState(listing)
  const isAwaitingDecision = state === 'pending' || state === 'submitted'
  const isSpotPriceOffer = getIsSpotPriceOffer(listing)
  const isContract = !!listing.contract
  // The end date is what the header reports once the window has closed.
  const endDate =
    state === 'live' || state === 'submitted'
      ? parseOptionalISODate(offering?.offerEndTime)
      : null

  const breakdown = isSpotPriceOffer ? (
    isLoggedIn ? (
      <TlcBreakdown
        // Remount so the expansion preference re-applies when the offer
        // lifecycle advances in place, without a page load.
        key={String(isAwaitingDecision)}
        amountLabelId={`${ns}.tlcBreakdown.amount`}
        basis={basis}
        bstockFee={bstockFee}
        currency={currency}
        defaultExpanded={isAwaitingDecision}
        feePercent={feePercent}
        isWithoutShipping={isWithoutShipping}
        suppressShippingLine={suppressShippingLine}
        onToggle={() =>
          trackButtonClicked({
            buttonName: 'total_landed_cost_breakdown',
            ...getOfferAnalytics(),
          })
        }
        shippingCents={shippingCents}
        taxInfo={tlcTaxInfo}
        tooltipId={`${ns}.tlcBreakdown.tooltip`}
        tooltipWithTaxId={`${ns}.tlcBreakdown.tooltipWithTax`}
        tooltipWithVatId={`${ns}.tlcBreakdown.tooltipWithVat`}
        totalLandedCostWithTax={totalLandedCostWithTax}
        vatInfo={tlcVatInfo}
      />
    ) : (
      <TlcBreakdownSignInNotice />
    )
  ) : null

  return (
    <ActionCard>
      <SimpleOfferHeader listing={listing} state={state} />

      {isLoggedIn && isSpotPriceOffer && (
        <TlcStats
          costPerUnitTooltipId={`${ns}.tlcStats.costPerUnitTooltip`}
          currency={currency}
          msrpPrice={msrpPrice}
          percentMsrpTooltipId={`${ns}.tlcStats.percentMsrpTooltip`}
          tlcPercentOfMsrpWithTax={tlcPercentOfMsrpWithTax}
          tlcPerUnitWithTax={tlcPerUnitWithTax}
          units={units}
        />
      )}

      <ActionCardBody>
        <SectionHeader>
          <FormattedMessage id={`${ns}.actions.header`} />
          <SectionInfoIcon
            size="small"
            tooltipProps={{ placement: 'top', hoverable: true }}
          >
            <FormattedMessage id={`${ns}.actions.tooltip`} />
          </SectionInfoIcon>
        </SectionHeader>

        {state === 'live' ? (
          <SimpleOfferActions listing={listing} selectedQuote={selectedQuote} />
        ) : (
          <SimpleOfferStatus listing={listing} state={state} />
        )}

        {breakdown}

        {state === 'submitted' && (
          <SimpleOfferSubmit listing={listing} selectedQuote={selectedQuote} />
        )}

        {isContract && <ContractInfoBanner />}
      </ActionCardBody>

      <ActionCardFooter
        endDate={endDate}
        endDateLabelId={`${ns}.footer.endDateLabel`}
        onReportProblem={() =>
          trackButtonClicked({
            buttonName: 'report_problem_with_listing',
            ...getOfferAnalytics(),
          })
        }
        reportProblemLabelId={`${ns}.footer.reportProblem`}
      />
    </ActionCard>
  )
}
